import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import {
  BaseApiResponseErrorsType,
  CampaignSetupResponseErrorType,
  CommonApiErrorActionType,
  PreparedApiErrorsType,
} from 'src/models/BaseApi';
import { CauseItemType, CauseSaveRequestType } from 'src/models/Causes';
import { DonationPageSaveFormType } from 'src/models/DonationPage/Form';
import { FaqItemType, FaqSaveRequestType } from 'src/models/DonationPage/Question';
import {
  DonationPageResponseType,
  DonationPageSectionsType,
  DonationPageTabsEnum,
} from 'src/models/DonationPage/Section';
import {
  DonationPageCurrentSectionStoreType,
  DonationPageGlobalStoreType,
  DonationPagePublishCheckPlusSubscriptionStoreType,
  DonationPagePublishDialogStoreType,
} from 'src/models/DonationPage/Store';
import { DonationPageSubmitCampaignRequestType } from 'src/models/DonationPage/SubmitCampaign';
import {
  CommonListCopyItemRequestType,
  CommonDeleteItemRequestType,
  CommonGetItemRequestType,
  CommonListItemType,
  CommonListReorderType,
  CommonListRequestType,
  CommonListType,
  CommonListCopyItemReducerType,
} from 'src/models/listTypes';
import { loadDonationPageMock } from 'src/pages/DonationSetup/containers/_mock';
import { BaseStoreEntityType, baseStoreEntityValue } from 'src/store/types';

export type DonationPageStateType = {
  activeFormName: DonationPageTabsEnum; // an active displaying form
  nextFormName: DonationPageTabsEnum; // save next form temporarily
  currentSectionData: DonationPageCurrentSectionStoreType | null; // the data from one out of three forms
  globalData: DonationPageGlobalStoreType | null; // common data of all the sections
  // save all errors and show them when a section changes
  rawValidationFormErrors: Array<BaseApiResponseErrorsType[0] & { TargetName: string }> | null;
  causes: {
    list: BaseStoreEntityType<CommonListType>;
    delete: BaseStoreEntityType<CommonDeleteItemRequestType>;
    copy: BaseStoreEntityType<CommonListItemType>;
    item: BaseStoreEntityType<CauseItemType>;
    save: BaseStoreEntityType<CommonListItemType>;
    reorder: BaseStoreEntityType<true>;
  };
  faq: {
    list: BaseStoreEntityType<CommonListType>;
    delete: BaseStoreEntityType<CommonDeleteItemRequestType>;
    copy: BaseStoreEntityType<CommonListItemType>;
    item: BaseStoreEntityType<FaqItemType>;
    save: BaseStoreEntityType<CommonListItemType>;
    reorder: BaseStoreEntityType<true>;
  };

  // actions
  saving: BaseStoreEntityType<true, PreparedApiErrorsType>; // info about saving
  validation: BaseStoreEntityType<'publish' | 'preview' | null> & {
    desiredDate: string | null;
  } & DonationPagePublishDialogStoreType & {
      plusSubscription: DonationPagePublishCheckPlusSubscriptionStoreType | null;
    };
  publish: BaseStoreEntityType<true>;
};

const initialState: DonationPageStateType = {
  activeFormName: DonationPageTabsEnum.FIRST,
  nextFormName: DonationPageTabsEnum.FIRST,
  currentSectionData: null,
  globalData: null,
  rawValidationFormErrors: null,
  causes: {
    list: baseStoreEntityValue(),
    delete: baseStoreEntityValue(),
    copy: baseStoreEntityValue(),
    item: baseStoreEntityValue(),
    save: baseStoreEntityValue(),
    reorder: baseStoreEntityValue(),
  },
  faq: {
    list: baseStoreEntityValue(),
    delete: baseStoreEntityValue(),
    copy: baseStoreEntityValue(),
    item: baseStoreEntityValue(),
    save: baseStoreEntityValue(),
    reorder: baseStoreEntityValue(),
  },

  saving: baseStoreEntityValue(),
  validation: {
    ...baseStoreEntityValue(),
    desiredDate: null,
    currencies: [],
    currencySelected: null,
    canEditCurrency: false,
    plusSubscription: null,
  },
  publish: baseStoreEntityValue(),
};

export const donationPageSlice = createSlice({
  name: 'donationPage',
  initialState,
  reducers: {
    saveFormStart: (state, { payload }: PayloadAction<DonationPageSaveFormType>) => {
      state.nextFormName = payload.nextFormName;
      state.saving = baseStoreEntityValue(null, true);
      state.causes = initialState.causes;
      state.faq = initialState.faq;
    },
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    saveFormOk: (state, { payload }: PayloadAction<DonationPageResponseType>) => {
      state.saving.loading = false;
      state.activeFormName = state.nextFormName;

      /** insert new data in store. be aware on localhost in dev */
      const { currentSectionData } = loadDonationPageMock(state.nextFormName, payload, true);
      state.currentSectionData = currentSectionData;

      if (!state.validation.loading) {
        state.saving.response = true;
      }
    },
    saveFormError: (state, { payload }: CommonApiErrorActionType) => {
      state.saving.loading = false;
      state.saving.error = payload;
    },
    saveClear: (state) => {
      state.saving.response = null;
      state.saving.error = null;
    },

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    validateStart: (state, { payload }: PayloadAction<{ id: string; mode: 'publish' | 'preview' }>) => {
      state.validation.loading = true;
      state.validation.response = null;

      state.validation.desiredDate = null;

      state.rawValidationFormErrors = null;
    },
    validateOk: (
      state,
      {
        payload,
      }: PayloadAction<
        { date: string | null; mode: 'publish' | 'preview' } & DonationPagePublishDialogStoreType & {
            plusSubscription: DonationPagePublishCheckPlusSubscriptionStoreType | null;
          }
      >
    ) => {
      state.validation.loading = false;
      state.validation.response = payload.mode;

      // this data only needed when publishing
      if (payload.mode === 'publish') {
        state.validation.desiredDate = payload.date;
        state.validation.currencies = payload.currencies;
        state.validation.currencySelected = payload.currencySelected;
        state.validation.canEditCurrency = payload.canEditCurrency;
        state.validation.plusSubscription = payload.plusSubscription;
      }
    },
    validateError: (state, { payload }: PayloadAction<CampaignSetupResponseErrorType>) => {
      state.validation.loading = false;
      state.validation.error = payload;
    },
    validateClear: (state) => {
      state.validation.error = null;
      state.validation.response = null;
    },

    changeValidationFormErrors: (
      state,
      { payload }: PayloadAction<Array<BaseApiResponseErrorsType[0] & { TargetName: string }> | null>
    ) => {
      state.rawValidationFormErrors = payload;
    },
    clearValidationFormErrorsBySection: (state, { payload }: PayloadAction<DonationPageTabsEnum>) => {
      state.rawValidationFormErrors = state.rawValidationFormErrors
        ? state.rawValidationFormErrors.filter(({ TargetName }) => TargetName !== payload)
        : null;
    },

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    publishStart: (state, { payload }: PayloadAction<DonationPageSubmitCampaignRequestType>) => {
      state.publish = baseStoreEntityValue();
    },
    publishOk: (state) => {
      state.publish = baseStoreEntityValue();
    },
    publishError: (state, { payload }: CommonApiErrorActionType) => {
      state.publish = baseStoreEntityValue(null, false, payload);
    },
    publishClear: (state) => {
      state.publish = baseStoreEntityValue();
    },

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    getCausesListStart: (state, { payload }: PayloadAction<CommonListRequestType>) => {
      state.causes.list.loading = true;
    },
    getCausesListOk: (state, { payload }: PayloadAction<CommonListType>) => {
      state.causes.list = baseStoreEntityValue(
        {
          hasMore: payload.hasMore,
          items: state.causes.list.response ? [...state.causes.list.response.items, ...payload.items] : payload.items,
        },
        false
      );
    },
    getCausesListError: (state, { payload }: CommonApiErrorActionType) => {
      state.causes.list = baseStoreEntityValue(null, false, payload);
    },

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    getFaqListStart: (state, { payload }: PayloadAction<CommonListRequestType>) => {
      state.faq.list.loading = true;
    },
    getFaqListOk: (state, { payload }: PayloadAction<CommonListType>) => {
      state.faq.list = baseStoreEntityValue(
        {
          hasMore: payload.hasMore,
          items: state.faq.list.response ? [...state.faq.list.response.items, ...payload.items] : payload.items,
        },
        false
      );
    },
    getFaqListError: (state, { payload }: CommonApiErrorActionType) => {
      state.faq.list = baseStoreEntityValue(null, false, payload);
    },

    reorderCausesListStart: (state, { payload: [, startIndex, endIndex] }: PayloadAction<CommonListReorderType>) => {
      state.causes.reorder = baseStoreEntityValue(null, true);

      if (state.causes.list.response?.items.length) {
        const [removed] = state.causes.list.response.items.splice(startIndex, 1);
        state.causes.list.response.items.splice(endIndex, 0, removed);
      }
    },
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    reorderCausesListOk: (state, { payload }: PayloadAction<CommonListReorderType>) => {
      state.causes.reorder = baseStoreEntityValue(true, true);
    },
    reorderCausesListError: (
      state,
      {
        payload: {
          error,
          data: [, startIndex, endIndex],
        },
      }: PayloadAction<CommonListCopyItemReducerType>
    ) => {
      state.causes.reorder = baseStoreEntityValue(null, true, error);

      /** rollback a transaction */
      if (state.causes.list.response?.items.length) {
        const [removed] = state.causes.list.response.items.splice(endIndex, 1);
        state.causes.list.response.items.splice(startIndex, 0, removed);
      }
    },

    reorderFaqListStart: (state, { payload: [, startIndex, endIndex] }: PayloadAction<CommonListReorderType>) => {
      state.faq.reorder = baseStoreEntityValue(null, true);

      if (state.faq.list.response?.items.length) {
        const [removed] = state.faq.list.response.items.splice(startIndex, 1);
        state.faq.list.response.items.splice(endIndex, 0, removed);
      }
    },
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    reorderFaqListOk: (state, { payload }: PayloadAction<CommonListReorderType>) => {
      state.faq.reorder = baseStoreEntityValue(true, true);
    },
    reorderFaqListError: (
      state,
      {
        payload: {
          error,
          data: [, startIndex, endIndex],
        },
      }: PayloadAction<CommonListCopyItemReducerType>
    ) => {
      state.faq.reorder = baseStoreEntityValue(null, true, error);

      /** rollback a transaction */
      if (state.faq.list.response?.items.length) {
        const [removed] = state.faq.list.response.items.splice(endIndex, 1);
        state.faq.list.response.items.splice(startIndex, 0, removed);
      }
    },

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    deleteCauseStart: (state, { payload }: PayloadAction<CommonDeleteItemRequestType>) => {
      state.causes.delete = baseStoreEntityValue(null, true);
    },
    deleteCauseOk: (state, { payload }: PayloadAction<CommonDeleteItemRequestType>) => {
      if (state.causes.list.response?.items.length) {
        const foundIndex = state.causes.list.response.items.findIndex(({ id }) => payload === id);

        if (foundIndex !== -1) {
          state.causes.list.response.items.splice(foundIndex, 1);
        }
      }

      state.causes.delete = baseStoreEntityValue(payload, false);
    },
    deleteCauseError: (state, { payload }: CommonApiErrorActionType) => {
      state.causes.delete = baseStoreEntityValue(null, false, payload);
    },
    deleteCauseClear: (state) => {
      state.causes.delete = baseStoreEntityValue(null, false, null);
    },

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    deleteFaqStart: (state, { payload }: PayloadAction<CommonDeleteItemRequestType>) => {
      state.faq.delete = baseStoreEntityValue(null, true);
    },
    deleteFaqOk: (state, { payload }: PayloadAction<CommonDeleteItemRequestType>) => {
      if (state.faq.list.response?.items.length) {
        const foundIndex = state.faq.list.response.items.findIndex(({ id }) => payload === id);

        if (foundIndex !== -1) {
          state.faq.list.response.items.splice(foundIndex, 1);
        }
      }

      state.faq.delete = baseStoreEntityValue(payload, false);
    },
    deleteFaqError: (state, { payload }: CommonApiErrorActionType) => {
      state.faq.delete = baseStoreEntityValue(null, false, payload);
    },
    deleteFaqClear: (state) => {
      state.faq.delete = baseStoreEntityValue(null, false, null);
    },

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    copyCauseStart: (state, { payload }: PayloadAction<CommonListCopyItemRequestType>) => {
      state.causes.copy = baseStoreEntityValue(null, true);
    },
    copyCauseOk: (state, { payload }: PayloadAction<CommonListItemType>) => {
      if (state.causes.list.response?.items.length) {
        state.causes.list.response.items.splice(payload.sequence - 1, 0, payload);
      }

      state.causes.copy = baseStoreEntityValue(payload, false);
    },
    copyCauseError: (state, { payload }: CommonApiErrorActionType) => {
      state.causes.copy = baseStoreEntityValue(null, false, payload);
    },

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    copyFaqStart: (state, { payload }: PayloadAction<CommonListCopyItemRequestType>) => {
      state.faq.copy = baseStoreEntityValue(null, true);
    },
    copyFaqOk: (state, { payload }: PayloadAction<CommonListItemType>) => {
      if (state.faq.list.response?.items.length) {
        state.faq.list.response.items.splice(payload.sequence - 1, 0, payload);
      }

      state.faq.copy = baseStoreEntityValue(payload, false);
    },
    copyFaqError: (state, { payload }: CommonApiErrorActionType) => {
      state.faq.copy = baseStoreEntityValue(null, false, payload);
    },

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    getCauseItemStart: (state, { payload }: PayloadAction<CommonGetItemRequestType>) => {
      state.causes.item = baseStoreEntityValue(null, true);
    },
    getCauseItemOk: (state, { payload }: PayloadAction<CauseItemType>) => {
      state.causes.item = baseStoreEntityValue(payload, false);
    },
    getCauseItemError: (state, { payload }: CommonApiErrorActionType) => {
      state.causes.item = baseStoreEntityValue(null, false, payload);
    },
    getCauseItemClear: (state) => {
      state.causes.item = baseStoreEntityValue(null, false, null);
    },

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    getFaqItemStart: (state, { payload }: PayloadAction<CommonGetItemRequestType>) => {
      state.faq.item = baseStoreEntityValue(null, true);
    },
    getFaqItemOk: (state, { payload }: PayloadAction<FaqItemType>) => {
      state.faq.item = baseStoreEntityValue(payload, false);
    },
    getFaqItemError: (state, { payload }: CommonApiErrorActionType) => {
      state.faq.item = baseStoreEntityValue(null, false, payload);
    },
    getFaqItemClear: (state) => {
      state.faq.item = baseStoreEntityValue(null, false, null);
    },

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    saveCauseItemStart: (state, { payload }: PayloadAction<CauseSaveRequestType>) => {
      state.causes.save = baseStoreEntityValue(null, true);
    },
    saveCauseItemOk: (state, { payload }: PayloadAction<CommonListItemType>) => {
      if (state.causes.list.response?.items instanceof Array) {
        const foundIndex = state.causes.list.response.items.findIndex(({ id }) => payload.id === id);

        if (foundIndex !== -1) {
          state.causes.list.response.items[foundIndex] = payload;
        } else {
          state.causes.list.response.items.unshift(payload);
        }
      }

      state.causes.save = baseStoreEntityValue(payload, false);
      state.causes.item = baseStoreEntityValue(null, false, null);
    },
    saveCauseItemError: (state, { payload }: CommonApiErrorActionType) => {
      state.causes.save = baseStoreEntityValue(null, false, payload);
    },
    saveCauseItemClear: (state) => {
      state.causes.save = baseStoreEntityValue(null, false);
    },

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    saveFaqItemStart: (state, { payload }: PayloadAction<FaqSaveRequestType>) => {
      state.faq.save = baseStoreEntityValue(null, true);
    },
    saveFaqItemOk: (state, { payload }: PayloadAction<CommonListItemType>) => {
      if (state.faq.list.response?.items instanceof Array) {
        const foundIndex = state.faq.list.response.items.findIndex(({ id }) => payload.id === id);

        if (foundIndex !== -1) {
          state.faq.list.response.items[foundIndex] = payload;
        } else {
          state.faq.list.response.items.unshift(payload);
        }
      }

      state.faq.save = baseStoreEntityValue(payload, false);
      state.faq.item = baseStoreEntityValue(null, false, null);
    },
    saveFaqItemError: (state, { payload }: CommonApiErrorActionType) => {
      state.faq.save = baseStoreEntityValue(null, false, payload);
    },
    saveFaqItemClear: (state) => {
      state.faq.save = baseStoreEntityValue(null, false);
    },

    changeCurrentSectionData: (
      state,
      {
        payload,
      }: PayloadAction<{
        currentSectionData: DonationPageCurrentSectionStoreType | null;
        globalData?: DonationPageGlobalStoreType | null;
        activeFormName?: DonationPageTabsEnum;
      }>
    ) => {
      state.currentSectionData = payload.currentSectionData;

      if (payload.activeFormName !== undefined) {
        state.activeFormName = payload.activeFormName;
      }

      if (payload.globalData !== undefined) {
        state.globalData = payload.globalData;
      }
    },

    changeGlobalData: (state, { payload }: PayloadAction<Partial<DonationPageGlobalStoreType>>) => {
      state.globalData = { ...state.globalData, ...(payload as DonationPageGlobalStoreType) };
    },

    changeTimeline: (
      state,
      {
        payload: { sections, checkInState = false },
      }: PayloadAction<{
        sections: Array<Partial<DonationPageSectionsType[0]> & { name: string }>;
        checkInState?: boolean;
      }>
    ) => {
      if (state.globalData) {
        const stateGlobal = state.globalData as DonationPageGlobalStoreType;

        sections.forEach((section) => {
          const foundIndex = stateGlobal.timeline.sections.findIndex(
            (iterableSection) => iterableSection.name === section.name
          );

          if (~foundIndex) {
            const previousErrorCount = stateGlobal.timeline.sections[foundIndex].errorCount;

            const newSectionData = {
              ...stateGlobal.timeline.sections[foundIndex],
              ...section,
            };

            if (checkInState && newSectionData.errorCount === 0 && previousErrorCount > 0) {
              newSectionData.errorCount = previousErrorCount;
            }

            stateGlobal.timeline.sections[foundIndex] = newSectionData;
          }
        });
      }
    },
  },
});
